
/* eslint-disable react/forbid-prop-types */
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

import PropTypes from 'prop-types';

import { t } from 'i18next';

import {
  styled, Box, Checkbox,
  Chip, CircularProgress, debounce, Paper, TextField,
} from '@mui/material';

import AutocompleteMui, { createFilterOptions } from '@mui/material/Autocomplete';

import { KeyboardArrowDown } from '@mui/icons-material';

import { useFormContext } from '../../../contexts/form';

import { Button } from '../Button';
import privateStyles from '../InputField/styles';
import { IconChecked, IconUnchecked } from '../CheckBox';

import useAutocomplete from './hooks';
import {
  styles, filterStyles, checkBoxStyles, autocompleteButtonStyles,
} from './styles';

const getOptionStyles = () => ({ '& > img': { mr: 1, flexShrink: 0 } });

const filter = createFilterOptions();

const AutocompleteRaw = (props) => {
  const {
    className,
    options,
    label,
    itemLabel,
    disabled,
    required,
    multiple,
    value,
    itemId,
    placeholder,
    onChangeAutocomplete,
    name,
    itemIcon,
    itemIcon2,
    virtual,
    fixLabel,
    hideLabel,
    autoCompleteProps,
    filterOptions,
    disabledOptions = [],
    freeSolo = false,
    itemColor,
    renderOptionEndIcon,
    renderOptionStartIcon,
    renderLabelEnd,
    renderPopperEnd,
    onFilteredOptions,
    onNewOptionClick,
    startAdornment,
  } = props;

  const {
    inputValue,
    onChange,
  } = useAutocomplete(
    name,
    value,
    options,
    itemId,
    onChangeAutocomplete,
    multiple,
    freeSolo,
    itemLabel,
  );
  const { formRegister, formErrors } = useFormContext();

  const inputRef = useRef();
  const [anchorEl, setAnchorEl] = useState();
  const [inputTextValue, setInputTextValue] = React.useState('');

  useEffect(() => {
    setAnchorEl(inputRef);
  }, [inputRef]);

  const fixTextLabel = (option) => {
    if (!option || typeof option !== 'object' || !option[itemLabel]) return '';
    const label = option[itemLabel];
    if (typeof label !== 'string') return String(label);
    return fixLabel ? t(label.replace('_', ' ').toLowerCase()) : t(label);
  };

  const handleVirtual = useCallback(
    debounce(virtual, 500),
    [virtual],
  );

  const labelComponent = (
    <>
      {t(label)}
      {renderLabelEnd?.({ inputRef: anchorEl })}
    </>
  );

  const PaperCustom = useCallback((paperProps) => (
    <Paper
      elevation={1}
      {...paperProps}
    >
      <Box>
        {paperProps.children}
        {
          renderPopperEnd && (
            renderPopperEnd()
          )
        }
      </Box>
    </Paper>
  ), [renderPopperEnd]);

  const register = formRegister(`${name}_aux`, {
    validate: () => {
      const validator = multiple
        ? inputValue.length > 0
        : inputValue !== null && inputValue !== '';
      return required ? validator : true;
    },
  });

  const register2 = formRegister(`${name}`, {
    validate: () => {
      const validator = multiple
        ? inputValue.length > 0
        : inputValue !== null && inputValue !== '';
      return required ? validator : true;
    },
  });

  const inputText = virtual && multiple ? inputTextValue : undefined;

  const getOptionLabel = (option) => (option ? fixTextLabel(option) : '');

  return (
    <Box ref={inputRef} className={`${className} DN-Autocomplete ${hideLabel && 'dn-hide-label'} ${!fixLabel && 'dn-no-fix'} ${freeSolo && 'dn-hide-popper'}`}>
      <AutocompleteMui
        {
          ...register
        }
        freeSolo={freeSolo}
        type="text"
        options={options || []}
        disabled={disabled}
        onChange={onChange(register2.onChange)}
        value={inputValue}
        multiple={multiple}
        noOptionsText={t('No options')}
        loadingText={t('Loading')}
        filterOptions={filterOptions}
        isOptionEqualToValue={(option, val) => val && option[itemId] === val[itemId]}
        renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          if (!option || typeof option !== 'object' || !option[itemId]) return null;

          return (
            <Chip
              key={option[itemId || 'id']}
              label={fixTextLabel(option)}
              {...tagProps}
              disabled={disabledOptions.indexOf(option[itemLabel]) !== -1 || disabled}
            />
          );
        })}
        inputValue={inputText}
        onInputChange={(event, changeValue, reason) => {
          if (virtual) {
            if (reason === 'input' && multiple) setInputTextValue(changeValue);
            handleVirtual(changeValue, event, reason);
          }

          const filteredOptions = filter(options, { inputValue: changeValue, getOptionLabel });
          onFilteredOptions?.({ filteredOptions, inputValue: changeValue, getOptionLabel });
        }}
        PaperComponent={PaperCustom}
        getOptionLabel={getOptionLabel}
        renderInput={(params) => (
          <TextField
            autoComplete="off"
            {...params}
            InputLabelProps={{ shrink: true }}
            label={labelComponent}
            placeholder={t(placeholder)}
            required={required}
            helperText={formErrors[`${name}_aux`] ? `${t(label)} ${t('IS_REQUIRED')}` : ''}
            error={!!formErrors[`${name}_aux`]}
            inputProps={{
              ...params.inputProps,
              _lpignore: 'true',
              'data-lpignore': true,
              autoComplete: 'new-password',
            }}

            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {startAdornment}
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <Box className="dn-endAdornment">
                  {autoCompleteProps?.loading ? <CircularProgress color="primary" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </Box>
              ),
            }}
          />
        )}
        popupIcon={<KeyboardArrowDown />}
        disableCloseOnSelect={multiple}
        renderOption={(optionProps, option, state) => {
          const { selected } = state;
          const key = option[itemId || 'id'] || `${optionProps.key}-${optionProps['data-option-index']}`;
          const optionStyles = getOptionStyles();

          const optionClasses = `${optionProps.className} ${option.className || ''} ${multiple ? 'dn-multiple' : 'dn-single'}`;

          if (option.optionType === 'NEW_OPTION') {
            return (
              <Button
                className={optionClasses}
                key={key}
                component="li"
                onClick={onNewOptionClick?.({ props: optionProps, option, state })}
                onMouseDown={(e) => e.preventDefault()}
                sx={autocompleteButtonStyles}
              >
                {t('Add "{{tagName}}"', { tagName: fixTextLabel(option) })}
              </Button>
            );
          }

          return (
            disabledOptions.indexOf(option[itemLabel]) === -1
            && (
              <Box {...optionProps} className={optionClasses} key={key} component="li" sx={optionStyles}>
                {
                  !freeSolo && multiple && (
                    <Checkbox
                      icon={<IconUnchecked />}
                      checkedIcon={<IconChecked />}
                      sx={checkBoxStyles}
                      checked={selected}
                    />
                  )
                }
                {
                  itemIcon && (
                    <img
                      className="dn-img-icon"
                      loading="lazy"
                      width="20"
                      src={option[itemIcon]}
                      alt=""
                    />
                  )
                }
                {
                  itemIcon2 && (
                    <img
                      className="dn-img-icon"
                      loading="lazy"
                      width="20"
                      src={option[itemIcon2]}
                      alt=""
                    />
                  )
                }
                {
                  itemColor && (
                    <Box sx={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '500px',
                      background: option[itemColor],
                      marginRight: '10px',
                      border: '1px solid rgba(40, 69, 210, 0.1)',
                    }}
                    />
                  )
                }
                {renderOptionStartIcon?.({ props: optionProps, option, state })}
                {fixTextLabel(option)}
                {renderOptionEndIcon?.({ props: optionProps, option, state })}
              </Box>
            )
          );
        }}
        {...autoCompleteProps}
      />
    </Box>
  );
};

AutocompleteRaw.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  options: PropTypes.array,
  itemLabel: PropTypes.string.isRequired,
  itemId: PropTypes.string.isRequired,
  itemIcon: PropTypes.string,
  itemIcon2: PropTypes.string,
  onChangeAutocomplete: PropTypes.func.isRequired,
  required: PropTypes.bool,
  multiple: PropTypes.bool,
  placeholder: PropTypes.string,
  virtual: PropTypes.func,
  fixLabel: PropTypes.bool,
  hideLabel: PropTypes.bool,
  autoCompleteProps: PropTypes.any,
  filterOptions: PropTypes.func,
  disabledOptions: PropTypes.array,
  freeSolo: PropTypes.bool,
  itemColor: PropTypes.string,
  renderOptionEndIcon: PropTypes.func,
  renderOptionStartIcon: PropTypes.func,
  renderLabelEnd: PropTypes.func,
  renderPopperEnd: PropTypes.func,
  onFilteredOptions: PropTypes.func,
  onNewOptionClick: PropTypes.func,
  startAdornment: PropTypes.node,
};

const Autocomplete = styled(styled(AutocompleteRaw)(privateStyles))(styles);
const AutocompleteFilter = styled(styled(styled(AutocompleteRaw)(privateStyles))(styles))(filterStyles);

Autocomplete.propTypes = AutocompleteRaw.propTypes;
AutocompleteFilter.propTypes = AutocompleteRaw.propTypes;

export default Autocomplete;
export { AutocompleteFilter, Autocomplete };
